import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useCalendarStore from '../../store/useCalendarStore';
import { toast } from 'sonner';
import { 
    Loader2, Users, Plus, Trash2, Search, Filter, 
    GraduationCap, ClipboardList, TrendingUp, X, 
    ChevronRight, BookOpen, UserPlus, Info, Database,
    CheckCircle, Edit3, Link2, Copy, QrCode, Clock,
    Mail, Phone, Calendar, Award, ArrowUpDown
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import { Skeleton } from '../ui/skeleton';
import api from '../../api';
import { BulkImportModal } from '../ui/BulkImportModal';
import { Upload } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Use REACT_APP_PUBLIC_URL for portal share links so QR codes work on external devices.
// Falls back to window.location.origin for local development.
const PUBLIC_ORIGIN = (process.env.REACT_APP_PUBLIC_URL || '').replace(/\/$/, '') || window.location.origin;

const formatDateToLabel = (dateStr) => {
    if (!dateStr) return '';
    const [yr, mo, dy] = dateStr.split('-');
    const dateObj = new Date(yr, mo - 1, dy);
    return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const TestScoreCell = ({ student, testCol, record, onUpdate }) => {
    const [val, setVal] = useState(record?.score ?? '');
    
    useEffect(() => {
        setVal(record?.score ?? '');
    }, [record?.score]);

    const isEnrolled = !student.subject_ids || !testCol.subject_id || student.subject_ids.includes(testCol.subject_id);

    if (!isEnrolled) {
        return (
            <td className="p-1.5 border-b border-r border-zinc-200 dark:border-zinc-800 text-center">
                <input 
                    type="text"
                    disabled
                    className="w-full h-8 rounded border border-transparent bg-zinc-50 dark:bg-zinc-900/30 text-center text-xs font-normal text-zinc-400 dark:text-zinc-500 cursor-not-allowed"
                    value="Not Enrolled"
                />
            </td>
        );
    }

    return (
        <td className="p-1.5 border-b border-r border-zinc-200 dark:border-zinc-800 text-center">
            <input 
                type="text"
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                className="w-full h-8 rounded border border-transparent hover:border-zinc-300 dark:hover:border-zinc-600 bg-zinc-50 dark:bg-zinc-800/50 focus:bg-white dark:focus:bg-zinc-900 text-center text-sm font-semibold transition-all outline-none focus:ring-2 focus:ring-blue-500"
                value={val}
                onChange={e => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setVal(value);
                    }
                }}
                placeholder="-"
                onBlur={() => {
                   if (val !== '' && parseFloat(val) !== record?.score) {
                       onUpdate(student.id, testCol.id, testCol.label, val);
                   }
                }}
            />
        </td>
    );
};

export default function StudentsPanel() {
    const [students, setStudents] = useState([]);
    const [batches, setBatches] = useState([]);
    const [branches, setBranches] = useState([]);
    const [courses, setCourses] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const filterCourse = useCalendarStore(s => s.studentsPanelFilterCourse);
    const setFilterCourse = useCalendarStore(s => s.setStudentsPanelFilterCourse);
    const filterBatch = useCalendarStore(s => s.studentsPanelFilterBatch);
    const setFilterBatch = useCalendarStore(s => s.setStudentsPanelFilterBatch);
    
    // Tab State
    const [activeTab, setActiveTab] = useState('overview');

    // Attendance States
    const [attendanceCols, setAttendanceCols] = useState([]);
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [loadingAttendance, setLoadingAttendance] = useState(false);

    // Test Scorer States
    const [testCols, setTestCols] = useState([]);
    const [allScores, setAllScores] = useState([]);
    const [loadingTestsData, setLoadingTestsData] = useState(false);

    // UI States
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [showMarksModal, setShowMarksModal] = useState(false);
    const [studentMarks, setStudentMarks] = useState([]);
    const [loadingMarks, setLoadingMarks] = useState(false);
    const [bulkImportOpen, setBulkImportOpen] = useState(false);

    // Share Link States
    const [shareModal, setShareModal] = useState(null); // { colId, label }
    const [shareExpiry, setShareExpiry] = useState(120);
    const [shareLink, setShareLink] = useState('');
    const [loadingShare, setLoadingShare] = useState(false);

    // Test Score Share Link States
    const [shareTestModal, setShareTestModal] = useState(null); // { colId, label }
    const [shareTestExpiry, setShareTestExpiry] = useState(120);
    const [shareTestLink, setShareTestLink] = useState('');
    const [shareTestCode, setShareTestCode] = useState('');
    const [loadingShareTest, setLoadingShareTest] = useState(false);

    // Form States
    const [newStudent, setNewStudent] = useState({
        name: '', email: '', branch_id: '', batch_id: '', course_id: '', enrollment_number: '', subject_ids: null
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [editStudent, setEditStudent] = useState({
        id: '', name: '', email: '', branch_id: '', batch_id: '', course_id: '', enrollment_number: '', subject_ids: null
    });
    const [newMark, setNewMark] = useState({
        subject_id: '', test_name: '', marks_obtained: '', total_marks: ''
    });

    const [showTestModal, setShowTestModal] = useState(false);
    const [testForm, setTestForm] = useState({ id: null, label: '', batch_id: '', subject_id: '', total_marks: 100 });
    
    const [showAttModal, setShowAttModal] = useState(false);
    const [attForm, setAttForm] = useState({ label: '', batch_id: '', subject_id: '', date: '' });

    // Sorting States
    const sortBy = useCalendarStore(s => s.studentsPanelSortBy);
    const setSortBy = useCalendarStore(s => s.setStudentsPanelSortBy);
    const [sortOpen, setSortOpen] = useState(false);
    const sortDropdownRef = React.useRef(null);

    const sortOptions = useMemo(() => [
        { value: 'name-asc', label: 'Name (A-Z)', category: 'Lexicographical' },
        { value: 'name-desc', label: 'Name (Z-A)', category: 'Lexicographical' },
        { value: 'score-desc', label: 'Score (High to Low)', category: 'Progress' },
        { value: 'score-asc', label: 'Score (Low to High)', category: 'Progress' },
        { value: 'attendance-desc', label: 'Attendance (High to Low)', category: 'Progress' },
        { value: 'attendance-asc', label: 'Attendance (Low to High)', category: 'Progress' },
        { value: 'risk-desc', label: 'Risk (High to Low)', category: 'Progress' },
        { value: 'risk-asc', label: 'Risk (Low to High)', category: 'Progress' }
    ], []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
                setSortOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);



    const fetchData = async () => {
        setLoading(true);
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;

            if (!team_id) {
                setLoading(false);
                return;
            }

            const [stuRes, batRes, braRes, subRes, couRes] = await Promise.all([
                supabase.from('students').select('*').eq('team_id', team_id),
                supabase.from('batches').select('*').eq('team_id', team_id),
                supabase.from('branches').select('*').eq('team_id', team_id),
                supabase.from('subjects').select('*').eq('team_id', team_id),
                supabase.from('courses').select('*').eq('team_id', team_id)
            ]);

            if (stuRes.error) throw stuRes.error;
            
            const fetchedBatches = batRes.data || [];
            const fetchedBranches = braRes.data || [];
            const fetchedCourses = couRes.data || [];
            
            // Manually "join" batch, branch, and course names
            const studentsWithJoins = (stuRes.data || []).map(s => ({
                ...s,
                batch: fetchedBatches.find(b => b.id === s.batch_id),
                branch: fetchedBranches.find(b => b.id === s.branch_id),
                course: fetchedCourses.find(c => c.id === s.course_id)
            }));

            setStudents(studentsWithJoins);
            
            // Sync selected student with fresh data
            setSelectedStudent(prev => {
                if (!prev) return null;
                return studentsWithJoins.find(s => s.id === prev.id) || prev;
            });

            setBatches(fetchedBatches);
            setBranches(fetchedBranches);
            setCourses(fetchedCourses);
            setSubjects(subRes.data || []);
        } catch (err) {
            console.error('Error fetching data:', err);
            toast.error('Failed to load students data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);



    // Set default batch once loaded
    useEffect(() => {
        if (!filterBatch && batches.length > 0) {
            setFilterBatch(batches[0].id);
        }
    }, [batches, filterBatch]);

    const handleAddStudent = async (e) => {
        e.preventDefault();
        try {
            // Get current team_id from profile
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;

            if (!team_id) throw new Error("No team context found");

            const { data, error } = await supabase.from('students').insert([{
                ...newStudent,
                team_id
            }]).select();

            if (error) throw error;
            toast.success('Student added successfully');
            setShowAddModal(false);
            setNewStudent({ name: '', email: '', branch_id: '', batch_id: '', course_id: '', enrollment_number: '', subject_ids: null });
            fetchData();
        } catch (err) {
            toast.error(err.message || 'Failed to add student');
        }
    };

    const handleEditStudentClick = (student) => {
        setEditStudent({
            id: student.id,
            name: student.name,
            email: student.email || '',
            branch_id: student.branch_id || '',
            batch_id: student.batch_id || '',
            course_id: student.course_id || '',
            enrollment_number: student.enrollment_number || '',
            subject_ids: student.subject_ids || null
        });
        setShowEditModal(true);
    };

    const handleEditStudent = async (e) => {
        e.preventDefault();
        try {
            await api.put(`/api/students/${editStudent.id}`, {
                name: editStudent.name,
                email: editStudent.email || null,
                branch_id: editStudent.branch_id || null,
                batch_id: editStudent.batch_id || null,
                course_id: editStudent.course_id || null,
                enrollment_number: editStudent.enrollment_number || null,
                subject_ids: editStudent.subject_ids
            });

            toast.success('Student details updated successfully');
            setShowEditModal(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || 'Failed to update student');
        }
    };

    const handleDeleteStudent = async (id) => {
        if (!window.confirm('Are you sure you want to delete this student?')) return;
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;
            const { error } = await supabase.from('students').delete().eq('id', id).eq('team_id', team_id);
            if (error) throw error;
            toast.success('Student deleted');
            fetchData();
        } catch (err) {
            toast.error('Failed to delete student');
        }
    };

    const handleDeleteColumn = async (id) => {
        if (!window.confirm('Are you sure you want to delete this attendance column? All records for this day will be permanently removed.')) return;
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;
            const { error } = await supabase.from('attendance_records').delete().eq('id', id).eq('team_id', team_id);
            if (error) throw error;
            toast.success('Column deleted');
            fetchAttendance();
        } catch (err) {
            toast.error('Failed to delete column');
        }
    };

    const handleDeleteTestColumn = async (id) => {
        if (!window.confirm('Are you sure you want to delete this test column? All scores for this test will be permanently removed.')) return;
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;
            const { error } = await supabase.from('test_records').delete().eq('id', id).eq('team_id', team_id);
            if (error) throw error;
            toast.success('Test deleted');
            fetchAllTestsData();
        } catch (err) {
            toast.error('Failed to delete test');
        }
    };

    const handleDeleteMark = async (id, studentId) => {
        if (!window.confirm('Are you sure you want to delete this test record?')) return;
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;
            const { error } = await supabase.from('test_scores').delete().eq('id', id).eq('team_id', team_id);
            if (error) throw error;
            toast.success('Record deleted');
            fetchMarks(studentId);
            updateStudentMetrics(studentId);
            if (activeTab === 'tests') fetchAllTestsData();
        } catch (err) {
            toast.error('Failed to delete record');
        }
    };

    const fetchMarks = async (studentId) => {
        setLoadingMarks(true);
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;
            const { data, error } = await supabase
                .from('students')
                .select('performance_data')
                .eq('id', studentId)
                .eq('team_id', team_id)
                .single();
            
            if (error) throw error;
            setStudentMarks(data?.performance_data || []);
        } catch (err) {
            toast.error('Failed to load marks');
        } finally {
            setLoadingMarks(false);
        }
    };

    const handleAddMark = async (e) => {
        e.preventDefault();
        try {
            if (!newMark.subject_id || !newMark.test_name || !newMark.marks_obtained) {
                toast.error('Please fill all required fields');
                return;
            }

            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;
            const today = new Date().toISOString().split('T')[0];

            // 1. Find or create a test_record for this session
            let { data: testRec, error: fetchErr } = await supabase.from('test_records')
                .select('id')
                .eq('name', newMark.test_name)
                .eq('batch_id', selectedStudent.batch_id)
                .eq('subject_id', newMark.subject_id)
                .eq('date', today)
                .eq('team_id', team_id)
                .maybeSingle();
            
            if (fetchErr) throw fetchErr;

            let testId;
            if (!testRec) {
                const { data: created, error: createErr } = await supabase.from('test_records').insert([{
                    name: newMark.test_name,
                    batch_id: selectedStudent.batch_id,
                    subject_id: newMark.subject_id,
                    date: today,
                    total_marks: parseFloat(newMark.total_marks || 100),
                    scores_data: [],
                    team_id
                }]).select('id').single();
                if (createErr) throw createErr;
                testId = created.id;
            } else {
                testId = testRec.id;
            }

            // 2. Use backend API to update score and sync performance
            const res = await api.post(`/api/test-records/${testId}/marks`, {
                marks: [{ 
                    student_id: selectedStudent.id, 
                    score: parseFloat(newMark.marks_obtained),
                    remarks: "" 
                }]
            });
            
            if (res.error) throw res.error;

            toast.success('Performance record updated');
            setNewMark({ subject_id: '', test_name: '', marks_obtained: '', total_marks: '100' });
            fetchMarks(selectedStudent.id);
            fetchStudents(); // Refresh students to get updated performance_data and overall_score
            if (activeTab === 'tests') fetchAllTestsData();
        } catch (err) {
            console.error(err);
            toast.error('Failed to add mark');
        }
    };

    const fetchAttendance = async () => {
        setLoadingAttendance(true);
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;

            const { data: records, error } = await supabase.from('attendance_records')
                .select('*, batch:batches(name)')
                .eq('team_id', team_id)
                .order('date', { ascending: true });

            if (error) throw error;
            
            // Manually link subjects
            const recordsWithSubjects = (records || []).map(r => ({
                ...r,
                subject: subjects.find(s => s.id === r.subject_id)
            }));
            
            setAttendanceCols(recordsWithSubjects || []);
            
            const flattened = (records || []).flatMap(rec => {
                const data = rec.attendance_data;
                if (!data) return [];
                if (Array.isArray(data)) {
                    return data.map(entry => ({
                        student_id: entry.student_id,
                        column_id: rec.id,
                        status: entry.status
                    }));
                } else if (typeof data === 'object') {
                    return Object.entries(data).map(([studentId, status]) => ({
                        student_id: studentId,
                        column_id: rec.id,
                        status: status
                    }));
                }
                return [];
            });
            setAttendanceRecords(flattened);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load attendance data');
        } finally {
            setLoadingAttendance(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'attendance') {
            fetchAttendance();
        } else if (activeTab === 'tests') {
            fetchAllTestsData();
        }
    }, [activeTab]);

    const fetchAllTestsData = async () => {
        setLoadingTestsData(true);
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;

            const { data: records, error } = await supabase.from('test_records')
                .select('*, batch:batches(name)')
                .eq('team_id', team_id)
                .order('date', { ascending: true });

            if (error) throw error;
            
            // Manually link subjects
            const recordsWithSubjects = (records || []).map(r => ({
                ...r,
                subject: subjects.find(s => s.id === r.subject_id)
            }));
            
            setTestCols(recordsWithSubjects || []);
            
            // Flatten scores for the matrix view
            const flattened = (records || []).flatMap(rec => {
                const data = rec.scores_data;
                if (!data) return [];
                if (Array.isArray(data)) {
                    return data.map(entry => ({
                        student_id: entry.student_id,
                        column_id: rec.id,
                        score: entry.score
                    }));
                } else if (typeof data === 'object') {
                    return Object.entries(data).map(([studentId, score]) => ({
                        student_id: studentId,
                        column_id: rec.id,
                        score: score
                    }));
                }
                return [];
            });
            setAllScores(flattened);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load tests data');
        } finally {
            setLoadingTestsData(false);
        }
    };

    const handleAddTestColumn = () => {
        setTestForm({ id: null, label: '', batch_id: '', subject_id: '', total_marks: 100 });
        setShowTestModal(true);
    };

    const handleRenameTestColumn = (colId, currentName, currentBatch, currentSubject, currentTotal) => {
        setTestForm({ 
            id: colId, 
            label: currentName, 
            batch_id: currentBatch || '', 
            subject_id: currentSubject || '', 
            total_marks: currentTotal || 100 
        });
        setShowTestModal(true);
    };

    const submitTestForm = async (e) => {
        e.preventDefault();
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;
            
            const today = new Date().toISOString().split('T')[0];

            if (testForm.id) {
                const { error } = await supabase.from('test_records')
                    .update({ 
                        name: testForm.label, 
                        batch_id: testForm.batch_id || null, 
                        subject_id: testForm.subject_id || null, 
                        total_marks: testForm.total_marks 
                    })
                    .eq('id', testForm.id)
                    .eq('team_id', team_id);
                if (error) throw error;
                toast.success('Test updated');
            } else {
                const { error } = await supabase.from('test_records').insert([{
                    name: testForm.label,
                    date: today,
                    batch_id: testForm.batch_id || null, 
                    subject_id: testForm.subject_id || null, 
                    total_marks: testForm.total_marks,
                    scores_data: [],
                    team_id
                }]);
                if (error) throw error;
                toast.success('Test added');
            }
            setShowTestModal(false);
            fetchAllTestsData();
        } catch (err) {
            toast.error('Failed to save test');
        }
    };

    const handleUpdateMarkInline = async (studentId, colId, colLabel, newScoreRaw) => {
        try {
            const newScore = parseFloat(newScoreRaw);
            if (isNaN(newScore)) return;

            const res = await api.post(`/api/test-records/${colId}/marks`, {
                marks: [{ student_id: studentId, score: newScore }]
            });
            
            if (res.error) throw res.error;
            
            // Re-fetch to ensure performance sync is reflected
            fetchAllTestsData();
            fetchStudents(); // Also refresh students to get updated performance_data
            toast.success("Score updated");
        } catch (err) {
            toast.error('Failed to update score');
        }
    };

    const handleAddAttendanceColumn = () => {
        if (!filterBatch) {
            toast.error('Please select a batch first');
            return;
        }
        const todayStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const todayDate = new Date().toISOString().split('T')[0];
        setAttForm({ 
            label: todayStr, 
            batch_id: filterBatch, 
            subject_id: '',
            date: todayDate
        });
        setShowAttModal(true);
    };



    const handleShareLink = async (colId, expiryMinutes) => {
        setLoadingShare(true);
        setShareLink('');
        try {
            // Use the centralized 'api' instance which handles the Auth token automatically
            const res = await api.post('/api/attendance/generate-link', {
                column_id: colId, 
                expires_in_minutes: expiryMinutes 
            });
            
            const data = res.data;
            const url = `${PUBLIC_ORIGIN}/attendance?token=${encodeURIComponent(data.token)}`;
            setShareLink(url);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || 'Failed to generate link');
        } finally {
            setLoadingShare(false);
        }
    };

    const handleShareTestLink = async (colId, expiryMinutes) => {
        setLoadingShareTest(true);
        setShareTestLink('');
        setShareTestCode('');
        try {
            const res = await api.post('/api/tests/generate-link', {
                column_id: colId, 
                expires_in_minutes: expiryMinutes 
            });
            
            const data = res.data;
            const url = `${PUBLIC_ORIGIN}/tests?token=${encodeURIComponent(data.token)}`;
            setShareTestLink(url);
            setShareTestCode(data.code);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || 'Failed to generate link');
        } finally {
            setLoadingShareTest(false);
        }
    };

    const handleCopyTestLink = () => {
        if (!shareTestLink) return;
        navigator.clipboard.writeText(shareTestLink);
        toast.success('Link copied to clipboard!');
    };

    const handleCopyLink = () => {
        if (!shareLink) return;
        navigator.clipboard.writeText(shareLink);
        toast.success('Link copied to clipboard!');
    };

    const handleRenameColumn = (colId, currentLabel, currentBatch, currentSubject, currentDate) => {
        setAttForm({ 
            id: colId, 
            label: currentLabel, 
            batch_id: currentBatch || '', 
            subject_id: currentSubject || '',
            date: currentDate ? currentDate.split('T')[0] : new Date().toISOString().split('T')[0]
        });
        setShowAttModal(true);
    };

    const submitAttForm = async (e) => {
        e.preventDefault();
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;
            
            const today = new Date().toISOString().split('T')[0];

            if (attForm.id) {
                const { error } = await supabase.from('attendance_records')
                    .update({ 
                        label: attForm.label, 
                        batch_id: attForm.batch_id || null, 
                        subject_id: attForm.subject_id || null,
                        date: attForm.date || today
                    })
                    .eq('id', attForm.id)
                    .eq('team_id', team_id);
                if (error) throw error;
                toast.success('Column updated');
            } else {
                const { error } = await supabase.from('attendance_records').insert([{
                    label: attForm.label,
                    date: attForm.date || today,
                    batch_id: attForm.batch_id || null, 
                    subject_id: attForm.subject_id || null,
                    attendance_data: {},
                    team_id
                }]);
                if (error) throw error;
                toast.success('Column added');
            }
            setShowAttModal(false);
            fetchAttendance();
        } catch (err) {
            toast.error('Failed to save column');
        }
    };

    const handleToggleAttendance = async (studentId, colId, currentStatus) => {
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;
            
            const nextStatusMap = {
                'present': 'absent',
                'absent': 'late',
                'late': 'present',
            };
            const newStatus = nextStatusMap[currentStatus || 'absent'];

            const col = attendanceCols.find(c => c.id === colId);
            if (!col) return;

            let newData;
            if (Array.isArray(col.attendance_data)) {
                newData = [...col.attendance_data];
                const idx = newData.findIndex(d => d.student_id === studentId);
                if (idx >= 0) {
                    newData[idx] = { ...newData[idx], status: newStatus };
                } else {
                    newData.push({ student_id: studentId, status: newStatus });
                }
            } else {
                newData = col.attendance_data ? { ...col.attendance_data } : {};
                newData[studentId] = newStatus;
            }

            const { error } = await supabase.from('attendance_records')
                .update({ attendance_data: newData })
                .eq('id', colId)
                .eq('team_id', team_id);
            
            if (error) throw error;
            
            // Optimistic updates
            setAttendanceCols(prev => prev.map(c => c.id === colId ? { ...c, attendance_data: newData } : c));
            setAttendanceRecords(prev => {
                const exists = prev.find(r => r.student_id === studentId && r.column_id === colId);
                if (exists) {
                    return prev.map(r => (r.student_id === studentId && r.column_id === colId) ? { ...r, status: newStatus } : r);
                } else {
                    return [...prev, { student_id: studentId, column_id: colId, status: newStatus }];
                }
            });
        } catch (err) {
            toast.error('Failed to update attendance');
        }
    };

    const filteredBatchesForFilter = useMemo(() => {
        if (!filterCourse) return batches;
        return batches.filter(b => b.course_id === filterCourse);
    }, [batches, filterCourse]);

    const filteredStudents = useMemo(() => {
        const filtered = students.filter(s => {
            const matchesSearch = s.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                 s.enrollment_number?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesCourse = !filterCourse || s.course_id === filterCourse || s.batch?.course_id === filterCourse;
            const matchesBatch = !filterBatch || s.batch_id === filterBatch;
            return matchesSearch && matchesCourse && matchesBatch;
        });

        return [...filtered].sort((a, b) => {
            if (sortBy === 'name-asc') {
                return (a.name || '').localeCompare(b.name || '');
            }
            if (sortBy === 'name-desc') {
                return (b.name || '').localeCompare(a.name || '');
            }
            if (sortBy === 'score-desc') {
                return Number(b.overall_score || 0) - Number(a.overall_score || 0);
            }
            if (sortBy === 'score-asc') {
                return Number(a.overall_score || 0) - Number(b.overall_score || 0);
            }
            if (sortBy === 'attendance-desc') {
                return Number(b.attendance_pct || 0) - Number(a.attendance_pct || 0);
            }
            if (sortBy === 'attendance-asc') {
                return Number(a.attendance_pct || 0) - Number(b.attendance_pct || 0);
            }
            if (sortBy === 'risk-desc') {
                const riskVal = { 'high': 3, 'medium': 2, 'low': 1 };
                const aVal = riskVal[a.risk_level] || 1;
                const bVal = riskVal[b.risk_level] || 1;
                return bVal - aVal;
            }
            if (sortBy === 'risk-asc') {
                const riskVal = { 'high': 3, 'medium': 2, 'low': 1 };
                const aVal = riskVal[a.risk_level] || 1;
                const bVal = riskVal[b.risk_level] || 1;
                return aVal - bVal;
            }
            return 0;
        });
    }, [students, searchQuery, filterCourse, filterBatch, sortBy]);

    const filteredAttendanceCols = useMemo(() => {
        return attendanceCols.filter(col => col.batch_id === filterBatch);
    }, [attendanceCols, filterBatch]);

    const filteredTestCols = useMemo(() => {
        return testCols.filter(col => col.batch_id === filterBatch);
    }, [testCols, filterBatch]);

    // Students visible in Attendance tab: must be enrolled in at least one visible column
    const attendanceStudents = useMemo(() => {
        const subjectCols = filteredAttendanceCols.filter(col => col.subject_id);
        if (subjectCols.length === 0) return filteredStudents;
        return filteredStudents.filter(student => {
            if (!student.subject_ids) return true; // enrolled in all subjects
            return subjectCols.some(col => student.subject_ids.includes(col.subject_id));
        });
    }, [filteredStudents, filteredAttendanceCols]);

    // Students visible in Tests tab: must be enrolled in at least one visible test column
    const testStudents = useMemo(() => {
        const subjectCols = filteredTestCols.filter(col => col.subject_id);
        if (subjectCols.length === 0) return filteredStudents;
        return filteredStudents.filter(student => {
            if (!student.subject_ids) return true; // enrolled in all subjects
            return subjectCols.some(col => student.subject_ids.includes(col.subject_id));
        });
    }, [filteredStudents, filteredTestCols]);

    const currentBatchForAtt = useMemo(() => {
        const targetBatchId = attForm.id ? attForm.batch_id : filterBatch;
        return batches.find(b => b.id === targetBatchId);
    }, [batches, attForm.id, attForm.batch_id, filterBatch]);

    const filteredSubjectsForAtt = useMemo(() => {
        if (!currentBatchForAtt?.course_id) return subjects;
        return subjects.filter(s => s.course_id === currentBatchForAtt.course_id);
    }, [subjects, currentBatchForAtt]);

    const fetchStudents = fetchData;

    // Auto-select first student when Overview tab is active
    useEffect(() => {
        if (activeTab === 'overview') {
            if (filteredStudents.length > 0) {
                const isStillInList = selectedStudent && filteredStudents.some(s => s.id === selectedStudent.id);
                if (!isStillInList) {
                    const firstStudent = filteredStudents[0];
                    setSelectedStudent(firstStudent);
                    fetchMarks(firstStudent.id);
                }
            } else {
                setSelectedStudent(null);
            }
        }
    }, [activeTab, filteredStudents, selectedStudent]);

    return (
        <>
        <BulkImportModal
            open={bulkImportOpen}
            onOpenChange={setBulkImportOpen}
            entityName="students"
            onImportComplete={() => {
                 // Refresh students list
                 if (typeof fetchData === 'function') fetchData();
                 else if (typeof fetchStudents === 'function') fetchStudents();
                 // Fallback to reload if no simple refresh
                 else window.location.reload();
            }}
        />
        <div className="flex flex-col h-full bg-zinc-50 dark:bg-zinc-950 overflow-hidden">
            {/* Header Area */}
            <div className="px-6 pt-6 pb-0 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 flex items-center gap-2">
                            <GraduationCap className="w-7 h-7 text-blue-600" />
                            Student Management
                        </h1>
                        <p className="text-zinc-500 text-sm mt-1">Manage enrollments, track academic progress and maintain records.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => setBulkImportOpen(true)}
                            className="flex items-center justify-center gap-2 border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200 px-4 py-2 rounded-lg font-medium transition-all shadow-sm active:scale-95"
                            title="Bulk Import"
                        >
                            <Upload className="w-4 h-4" />
                            Bulk Import
                        </button>
                        <button 
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all shadow-sm active:scale-95"
                        >
                            <UserPlus className="w-4 h-4" />
                            Add New Student
                        </button>
                    </div>
                </div>

                <div className="flex gap-6 mt-6 border-b border-transparent">
                    <button 
                        onClick={() => setActiveTab('overview')}
                        className={cn(
                            "pb-3 font-medium text-sm transition-colors relative",
                            activeTab === 'overview' ? "text-blue-600 dark:text-blue-500" : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                        )}
                    >
                        Overview
                        {activeTab === 'overview' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-500 rounded-t-full" /> }
                    </button>
                    <button 
                        onClick={() => setActiveTab('attendance')}
                        className={cn(
                            "pb-3 font-medium text-sm transition-colors relative",
                            activeTab === 'attendance' ? "text-blue-600 dark:text-blue-500" : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                        )}
                    >
                        Attendance Viewer
                        {activeTab === 'attendance' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-500 rounded-t-full" /> }
                    </button>
                    <button 
                        onClick={() => setActiveTab('tests')}
                        className={cn(
                            "pb-3 font-medium text-sm transition-colors relative",
                            activeTab === 'tests' ? "text-blue-600 dark:text-blue-500" : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                        )}
                    >
                        Test Scorer
                        {activeTab === 'tests' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-500 rounded-t-full" /> }
                    </button>

                </div>
            </div>

            {/* Search and Filters */}
            <div className="px-6 pt-6">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input 
                            type="text"
                            placeholder="Search students by name or enrollment ID..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg text-sm focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                        />
                    </div>
                    <div className="flex gap-2">
                        <div className="relative">
                            <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                            <select 
                                value={filterCourse || ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFilterCourse(val);
                                    const relatedBatches = val ? batches.filter(b => b.course_id === val) : batches;
                                    if (relatedBatches.length > 0) {
                                        setFilterBatch(relatedBatches[0].id);
                                    } else {
                                        setFilterBatch('');
                                    }
                                }}
                                className="pl-10 pr-8 py-2 bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg text-sm appearance-none focus:ring-2 focus:ring-blue-500 outline-none min-w-[160px]"
                            >
                                <option value="">All Courses</option>
                                {courses.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                            <select 
                                value={filterBatch || ''}
                                onChange={(e) => setFilterBatch(e.target.value)}
                                className="pl-10 pr-8 py-2 bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg text-sm appearance-none focus:ring-2 focus:ring-blue-500 outline-none min-w-[160px]"
                            >
                                <option value="">All Batches</option>
                                {filteredBatchesForFilter.map(b => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </select>
                        </div>
                        {/* Premium Sort Dropdown */}
                        <div className="relative" ref={sortDropdownRef}>
                            <button
                                type="button"
                                onClick={() => setSortOpen(!sortOpen)}
                                className="flex items-center gap-2 px-4 py-2 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-200 rounded-lg text-sm font-medium transition-all shadow-sm active:scale-95 outline-none min-w-[140px] justify-between h-full"
                            >
                                <div className="flex items-center gap-2">
                                    <ArrowUpDown className="w-4 h-4 text-zinc-400" />
                                    <span>Sort</span>
                                </div>
                                <span className="text-xs font-bold text-blue-600 dark:text-blue-500">
                                    {sortOptions.find(o => o.value === sortBy)?.label.split(' ')[0] || 'Name'}
                                </span>
                            </button>
                            
                            {sortOpen && (
                                <div className="absolute right-0 mt-2 w-60 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                                    <div className="px-3 py-2 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
                                        <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Sorting Options</span>
                                    </div>
                                    <div className="p-1.5 space-y-1">
                                        {/* Lexicographical Section */}
                                        <div className="px-2.5 py-1">
                                            <span className="text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">Name Lexicography</span>
                                        </div>
                                        {sortOptions.filter(o => o.category === 'Lexicographical').map(option => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => {
                                                    setSortBy(option.value);
                                                    setSortOpen(false);
                                                }}
                                                className={cn(
                                                    "w-full text-left px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-between transition-all",
                                                    sortBy === option.value
                                                        ? "bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-450"
                                                        : "text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/60"
                                                )}
                                            >
                                                {option.label}
                                                {sortBy === option.value && <div className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-450" />}
                                            </button>
                                        ))}

                                        {/* Separator */}
                                        <div className="h-px bg-zinc-100 dark:bg-zinc-800 my-1" />

                                        {/* Progress Options Section */}
                                        <div className="px-2.5 py-1">
                                            <span className="text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">Progress Type</span>
                                        </div>
                                        {sortOptions.filter(o => o.category === 'Progress').map(option => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => {
                                                    setSortBy(option.value);
                                                    setSortOpen(false);
                                                }}
                                                className={cn(
                                                    "w-full text-left px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-between transition-all",
                                                    sortBy === option.value
                                                        ? "bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-450"
                                                        : "text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/60"
                                                )}
                                            >
                                                {option.label}
                                                {sortBy === option.value && <div className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-450" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className={cn("flex-1 p-6 pt-4", activeTab === 'overview' ? "overflow-hidden flex flex-col" : "overflow-y-auto")}>
                {activeTab === 'overview' && (
                    loading ? (
                        <div className="flex flex-col lg:flex-row h-full gap-6 items-stretch overflow-hidden">
                            {/* Sidebar Skeletons */}
                            <div className="w-full lg:w-80 shrink-0 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-[2rem] flex flex-col overflow-hidden shadow-sm p-4 space-y-3">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="p-3 border border-zinc-100 dark:border-zinc-800/80 rounded-xl flex items-center gap-3">
                                        <Skeleton className="w-9 h-9 rounded-lg" />
                                        <div className="flex-1 space-y-1.5">
                                            <Skeleton className="h-4 w-3/4" />
                                            <Skeleton className="h-3 w-1/2" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Main Remaining Space Skeletons */}
                            <div className="flex-1 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-[2rem] p-6 space-y-6 shadow-sm">
                                <div className="flex items-center gap-4">
                                    <Skeleton className="w-12 h-12 rounded-xl" />
                                    <div className="space-y-2">
                                        <Skeleton className="h-5 w-48" />
                                        <Skeleton className="h-3.5 w-32" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-4">
                                    <div className="lg:col-span-4 space-y-4">
                                        <Skeleton className="h-40 w-full rounded-2xl" />
                                        <Skeleton className="h-48 w-full rounded-2xl" />
                                    </div>
                                    <div className="lg:col-span-8 space-y-4">
                                        <Skeleton className="h-12 w-full rounded-xl" />
                                        <Skeleton className="h-24 w-full rounded-2xl" />
                                        <Skeleton className="h-24 w-full rounded-2xl" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : filteredStudents.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center bg-white dark:bg-zinc-900 rounded-[2rem] border border-dashed border-zinc-200 dark:border-zinc-800 h-full">
                            <div className="w-16 h-16 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mb-4">
                                <Users className="w-8 h-8 text-zinc-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 font-bold">No students found</h3>
                            <p className="text-zinc-500 max-w-xs mx-auto mt-2 text-sm leading-relaxed">Adjust your filters or enroll a new student to see their detailed analytics here.</p>
                        </div>
                    ) : (
                        <div className="flex flex-col lg:flex-row h-full gap-6 items-stretch overflow-hidden">
                            {/* Student Side Panel List */}
                            <div className="w-full lg:w-80 shrink-0 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-[2rem] flex flex-col overflow-hidden shadow-sm">
                                <div className="px-5 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50 flex items-center justify-between">
                                    <span className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Enrolled Students ({filteredStudents.length})</span>
                                </div>
                                <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
                                    {filteredStudents.map(student => (
                                        <button
                                            key={student.id}
                                            onClick={() => {
                                                setSelectedStudent(student);
                                                fetchMarks(student.id);
                                            }}
                                            className={cn(
                                                "w-full text-left p-3 rounded-2xl flex items-center justify-between border transition-all active:scale-[0.98]",
                                                selectedStudent?.id === student.id
                                                    ? "bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800/60 shadow-sm"
                                                    : "bg-transparent border-transparent hover:bg-zinc-50 dark:hover:bg-zinc-800/40"
                                            )}
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className={cn(
                                                    "w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm shrink-0",
                                                    selectedStudent?.id === student.id
                                                        ? "bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md shadow-blue-500/20"
                                                        : "bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                                                )}>
                                                    {student.name.charAt(0)}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className={cn(
                                                        "text-sm font-bold truncate leading-tight",
                                                        selectedStudent?.id === student.id
                                                            ? "text-blue-600 dark:text-blue-400"
                                                            : "text-zinc-900 dark:text-zinc-100"
                                                    )}>{student.name}</p>
                                                    <p className="text-[10px] text-zinc-400 font-mono mt-0.5">{student.enrollment_number}</p>
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-end gap-1 shrink-0">
                                                <span className={cn(
                                                    "text-xs font-black",
                                                    (student.overall_score || 0) >= 75 ? "text-emerald-500" : (student.overall_score || 0) >= 50 ? "text-blue-500" : "text-red-500"
                                                )}>{student.overall_score || 0}%</span>
                                                <div className={cn(
                                                    "w-1.5 h-1.5 rounded-full",
                                                    student.risk_level === 'high' ? "bg-red-500 animate-pulse" : student.risk_level === 'medium' ? "bg-orange-500" : "bg-emerald-500"
                                                )} />
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Performance Details Main Remaining Space */}
                            <div className="flex-1 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-[2rem] flex flex-col overflow-hidden shadow-sm">
                                {selectedStudent ? (
                                    <div className="flex flex-col h-full overflow-hidden">
                                        {/* Header */}
                                        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/20">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white shadow-md shadow-blue-500/20">
                                                    <TrendingUp className="w-5 h-5" />
                                                </div>
                                                <div>
                                                    <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-50 tracking-tight leading-none">{selectedStudent.name}</h2>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 rounded-full">
                                                            ID: {selectedStudent.enrollment_number || 'N/A'}
                                                        </span>
                                                        {selectedStudent.course?.name && (
                                                            <span className="text-[9px] font-bold text-violet-500 uppercase tracking-widest bg-violet-50 dark:bg-violet-900/20 px-2 py-0.5 rounded-full">
                                                                {selectedStudent.course.name}
                                                            </span>
                                                        )}
                                                        {selectedStudent.batch?.name && (
                                                            <span className="text-[9px] font-bold text-blue-500 uppercase tracking-widest bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded-full">
                                                                {selectedStudent.batch.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-center gap-2">
                                                <button 
                                                    onClick={() => handleEditStudentClick(selectedStudent)}
                                                    className="flex items-center gap-1.5 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors active:scale-95"
                                                >
                                                    <Edit3 className="w-3.5 h-3.5" />
                                                    Edit Details
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteStudent(selectedStudent.id)}
                                                    className="flex items-center gap-1.5 bg-red-50 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-900/30 text-red-600 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors active:scale-95"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                    Unenroll Student
                                                </button>
                                            </div>
                                        </div>

                                        {/* Performance Content */}
                                        <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 xl:grid-cols-12 gap-6 custom-scrollbar">
                                            {/* Profile Card & Add Mark Form */}
                                            <div className="xl:col-span-4 space-y-6">
                                                {/* Profile Quick Stats */}
                                                <div className="bg-gradient-to-b from-zinc-50 to-white dark:from-zinc-800/30 dark:to-zinc-900 p-5 rounded-2xl border border-zinc-100 dark:border-zinc-800/80 shadow-sm relative overflow-hidden group">
                                                    <h3 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                                                        <Info className="w-3.5 h-3.5 text-zinc-400" /> Student Profile
                                                    </h3>
                                                    <div className="space-y-3">
                                                        <div className="flex items-center gap-3 p-2.5 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-100 dark:border-zinc-700/50 shadow-sm">
                                                            <div className="w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-400 shrink-0">
                                                                <Mail className="w-4 h-4" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-[8px] font-bold text-zinc-400 uppercase tracking-tighter leading-none mb-1">Email Address</p>
                                                                <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-200 truncate">{selectedStudent.email || 'No email provided'}</p>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-3 p-2.5 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-100 dark:border-zinc-700/50 shadow-sm">
                                                            <div className="w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-400 shrink-0">
                                                                <BookOpen className="w-4 h-4" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-[8px] font-bold text-zinc-400 uppercase tracking-tighter leading-none mb-1">Branch</p>
                                                                <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-200 truncate">{selectedStudent.branch?.name || 'No branch assigned'}</p>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-3 p-2.5 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-100 dark:border-zinc-700/50 shadow-sm">
                                                            <div className="w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-400 shrink-0">
                                                                <GraduationCap className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-[8px] font-bold text-zinc-400 uppercase tracking-tighter leading-none mb-1">Course</p>
                                                                <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-200 truncate">{selectedStudent.course?.name || 'No course assigned'}</p>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-start gap-3 p-2.5 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-100 dark:border-zinc-700/50 shadow-sm">
                                                            <div className="w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-400 shrink-0 mt-0.5">
                                                                <BookOpen className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-[8px] font-bold text-zinc-400 uppercase tracking-tighter leading-none mb-1">Enrolled Subjects</p>
                                                                <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-200 leading-normal">
                                                                    {!selectedStudent.subject_ids 
                                                                        ? "All Subjects" 
                                                                        : subjects
                                                                            .filter(s => s.course_id === selectedStudent.course_id && selectedStudent.subject_ids.includes(s.id))
                                                                            .map(s => s.name)
                                                                            .join(', ') || 'No subjects selected'
                                                                    }
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Quick Score and Risk Stats */}
                                                    <div className="mt-5 pt-5 border-t border-zinc-100 dark:border-zinc-800 grid grid-cols-2 gap-3">
                                                        <div className="text-center p-3 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-100 dark:border-zinc-700/50 shadow-sm">
                                                            <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">Avg Score</p>
                                                            <p className={cn(
                                                                "text-xl font-black",
                                                                (selectedStudent.overall_score || 0) >= 75 ? "text-emerald-500" : (selectedStudent.overall_score || 0) >= 50 ? "text-blue-500" : "text-red-500"
                                                            )}>
                                                                {selectedStudent.overall_score || 0}%
                                                            </p>
                                                        </div>
                                                        <div className="text-center p-3 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-100 dark:border-zinc-700/50 shadow-sm">
                                                            <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">Risk Status</p>
                                                            <p className={cn(
                                                                "text-xs font-black uppercase tracking-tight mt-1",
                                                                selectedStudent.risk_level === 'high' ? "text-red-500" : selectedStudent.risk_level === 'medium' ? "text-orange-500" : "text-emerald-500"
                                                            )}>
                                                                {selectedStudent.risk_level || 'Low'} Risk
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Add Mark Form */}
                                                <div className="bg-zinc-900 dark:bg-zinc-850 p-5 rounded-2xl shadow-lg relative overflow-hidden">
                                                    <h3 className="text-[10px] font-black text-white/60 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                                                        <Award className="w-4 h-4 text-white/80" /> Add Performance Entry
                                                    </h3>
                                                    <form onSubmit={handleAddMark} className="space-y-3.5">
                                                        <div>
                                                            <label className="block text-[9px] font-black text-white/40 uppercase tracking-widest mb-1 ml-0.5">Subject</label>
                                                            <select 
                                                                required
                                                                className="w-full bg-white/10 border border-white/10 rounded-xl py-2 px-3.5 text-xs text-white focus:bg-white/20 focus:ring-2 focus:ring-white/30 outline-none appearance-none transition-all"
                                                                value={newMark.subject_id || ''}
                                                                onChange={(e) => setNewMark({...newMark, subject_id: e.target.value})}
                                                            >
                                                                <option value="" className="text-zinc-900">Select Subject</option>
                                                                {subjects.map(s => <option key={s.id} value={s.id} className="text-zinc-900">{s.name}</option>)}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-[9px] font-black text-white/40 uppercase tracking-widest mb-1 ml-0.5">Test / Assessment Name</label>
                                                            <input 
                                                                required
                                                                type="text" 
                                                                className="w-full bg-white/10 border border-white/10 rounded-xl py-2 px-3.5 text-xs text-white focus:bg-white/20 focus:ring-2 focus:ring-white/30 outline-none transition-all placeholder:text-white/30"
                                                                value={newMark.test_name}
                                                                onChange={(e) => setNewMark({...newMark, test_name: e.target.value})}
                                                                placeholder="e.g. Midterm exam"
                                                            />
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <div>
                                                                <label className="block text-[9px] font-black text-white/40 uppercase tracking-widest mb-1 ml-0.5">Obtained</label>
                                                                <input 
                                                                    required
                                                                    type="text"
                                                                    inputMode="decimal"
                                                                    pattern="[0-9]*[.,]?[0-9]*"
                                                                    className="w-full bg-white/10 border border-white/10 rounded-xl py-2 px-3.5 text-xs text-white focus:bg-white/20 focus:ring-2 focus:ring-white/30 outline-none transition-all"
                                                                    value={newMark.marks_obtained}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                                                            setNewMark({...newMark, marks_obtained: value});
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-[9px] font-black text-white/40 uppercase tracking-widest mb-1 ml-0.5">Total</label>
                                                                <input 
                                                                    required
                                                                    type="text"
                                                                    inputMode="decimal"
                                                                    pattern="[0-9]*[.,]?[0-9]*"
                                                                    className="w-full bg-white/10 border border-white/10 rounded-xl py-2 px-3.5 text-xs text-white focus:bg-white/20 focus:ring-2 focus:ring-white/30 outline-none transition-all"
                                                                    value={newMark.total_marks || '100'}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                                                            setNewMark({...newMark, total_marks: value});
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <button 
                                                            type="submit"
                                                            className="w-full bg-white text-zinc-900 hover:bg-zinc-50 font-bold text-xs uppercase tracking-widest py-3 rounded-xl transition-all active:scale-[0.98] shadow-md mt-1"
                                                        >
                                                            Enroll Mark
                                                        </button>
                                                    </form>
                                                </div>
                                            </div>

                                            {/* Results History List (col-span-8) */}
                                            <div className="xl:col-span-8 flex flex-col min-h-0">
                                                <div className="flex items-center justify-between mb-4">
                                                    <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-1.5">
                                                        <ClipboardList className="w-4 h-4 text-blue-500" /> Assessment History
                                                    </h3>
                                                    <span className="text-[9px] font-bold text-zinc-400 bg-zinc-50 dark:bg-zinc-800/80 px-2.5 py-1 rounded-full uppercase tracking-wider">
                                                        Chronological
                                                    </span>
                                                </div>

                                                {loadingMarks ? (
                                                    <div className="flex flex-1 items-center justify-center py-12">
                                                        <div className="flex flex-col items-center gap-2">
                                                            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                                                            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Updating feed...</p>
                                                        </div>
                                                    </div>
                                                ) : studentMarks.length === 0 ? (
                                                    <div className="flex-1 bg-zinc-50/50 dark:bg-zinc-800/10 border-2 border-dashed border-zinc-100 dark:border-zinc-800/80 rounded-2xl flex flex-col items-center justify-center p-8 text-center min-h-[220px]">
                                                        <div className="w-10 h-10 bg-zinc-100 dark:bg-zinc-800 rounded-xl flex items-center justify-center text-zinc-300 mb-3">
                                                            <Award className="w-5 h-5" />
                                                        </div>
                                                        <h4 className="text-xs font-bold text-zinc-800 dark:text-zinc-200">No Assessment Records</h4>
                                                        <p className="text-[10px] text-zinc-500 max-w-[180px] mt-1 leading-normal">Submit a new result entry to populate this feed.</p>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-3 overflow-y-auto pr-1 custom-scrollbar animate-in fade-in duration-300">
                                                        {studentMarks.map((mark, idx) => {
                                                            const total = mark.total_marks || 100;
                                                            const percent = (mark.score / total) * 100;
                                                            return (
                                                                <div key={idx} className="group bg-white dark:bg-zinc-900 p-4 rounded-xl border border-zinc-100 dark:border-zinc-800/60 hover:border-blue-500/20 hover:shadow-md transition-all flex items-center justify-between relative overflow-hidden">
                                                                    <div className="absolute top-0 bottom-0 left-0 w-1 bg-zinc-100 dark:bg-zinc-800 group-hover:bg-blue-50 transition-all" />
                                                                    <div className="flex items-center gap-4 min-w-0">
                                                                        <div className="w-10 h-10 bg-zinc-50 dark:bg-zinc-800/50 rounded-xl flex flex-col items-center justify-center border border-zinc-100 dark:border-zinc-700/30 shrink-0">
                                                                            <Calendar className="w-3.5 h-3.5 text-zinc-400" />
                                                                            <span className="text-[7px] font-black text-zinc-400 uppercase mt-0.5">
                                                                                {new Date(mark.date).toLocaleDateString('en-US', { month: 'short' })}
                                                                            </span>
                                                                        </div>
                                                                        <div className="min-w-0">
                                                                            <p className="text-[9px] font-bold text-blue-500 uppercase tracking-wider leading-none">Assessment</p>
                                                                            <h4 className="text-sm font-bold text-zinc-900 dark:text-zinc-50 truncate mt-1">
                                                                                {mark.test_name || 'Unknown Assessment'}
                                                                            </h4>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-4 shrink-0">
                                                                        <div className="text-right">
                                                                            <div className="flex items-end gap-1 px-2.5 py-0.5 bg-zinc-50 dark:bg-zinc-800 rounded-lg border border-zinc-100 dark:border-zinc-700/50">
                                                                                <span className="text-base font-black text-zinc-900 dark:text-zinc-50 leading-none">{mark.score}</span>
                                                                                <span className="text-[10px] font-bold text-zinc-400 leading-none mb-0.5">/ {total}</span>
                                                                            </div>
                                                                            <div className={cn(
                                                                                "text-[8px] font-black mt-1.5 uppercase tracking-widest flex items-center justify-end gap-1",
                                                                                percent >= 80 ? "text-emerald-500" : percent >= 60 ? "text-blue-500" : percent >= 40 ? "text-orange-500" : "text-red-500"
                                                                            )}>
                                                                                <div className={cn(
                                                                                    "w-1 h-1 rounded-full",
                                                                                    percent >= 80 ? "bg-emerald-500" : percent >= 60 ? "bg-blue-500" : percent >= 40 ? "bg-orange-500" : "bg-red-500"
                                                                                )} />
                                                                                {percent >= 80 ? 'Distinction' : percent >= 60 ? 'First Class' : percent >= 40 ? 'Pass' : 'Failed'}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
                                        <div className="w-16 h-16 bg-blue-50/50 dark:bg-blue-900/20 rounded-[1.5rem] flex items-center justify-center text-blue-500 mb-4">
                                            <Users className="w-8 h-8" />
                                        </div>
                                        <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-50 mb-1">Select a Student</h3>
                                        <p className="text-xs text-zinc-500 max-w-xs leading-relaxed">
                                            Choose a student from the side panel list to view their comprehensive academic history, risk analysis, and add performance entries.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                )}

                {activeTab === 'attendance' && (
                    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden flex flex-col h-full shadow-sm">
                        <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex justify-between items-center bg-zinc-50/50 dark:bg-zinc-900/50">
                            <h2 className="font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 text-blue-500" />
                                Interactive Attendance Viewer
                            </h2>
                            <button 
                                onClick={handleAddAttendanceColumn}
                                className="flex items-center gap-1.5 bg-white border border-zinc-200 hover:bg-zinc-50 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm"
                            >
                                <Plus className="w-4 h-4" />
                                Add Day
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto">
                            <table className="w-full text-left border-collapse min-w-max relative">
                                <thead className="bg-zinc-50 dark:bg-zinc-800/50 sticky top-0 z-20 shadow-sm shadow-zinc-200/50 dark:shadow-black/20">
                                    <tr>
                                        <th className="p-3 border-b border-r border-zinc-200 dark:border-zinc-800 font-semibold text-xs tracking-wider text-zinc-500 uppercase sticky left-0 bg-zinc-50 dark:bg-zinc-900/95 backdrop-blur-sm z-30 w-64 min-w-[250px]">Student Name</th>
                                        {filteredAttendanceCols.map(col => (
                                            <th key={col.id} className="p-3 border-b border-r border-zinc-200 dark:border-zinc-800 font-semibold text-xs tracking-wider text-zinc-500 uppercase min-w-[120px] group">
                                                <div className="flex items-center gap-1.5 w-full">
                                                    <div className="truncate flex-1 flex flex-col">
                                                        <div className="flex items-center gap-1.5">
                                                            <span>{col.label}</span>
                                                            {col.type === 'test' && (
                                                                <span className="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 px-1 rounded text-[9px] font-bold">TEST</span>
                                                            )}
                                                        </div>
                                                        {(col.batch?.name || col.subject?.name) && (
                                                            <span className="text-[10px] text-zinc-400 font-normal truncate normal-case tracking-normal">
                                                                {[col.batch?.name, col.subject?.name].filter(Boolean).join(' • ')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button 
                                                        onClick={() => handleRenameColumn(col.id, col.label, col.batch_id, col.subject_id, col.date)}
                                                        className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-blue-500 transition-opacity"
                                                        title="Rename"
                                                    >
                                                        <Edit3 className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setShareModal({ colId: col.id, label: col.label });
                                                            setShareLink('');
                                                            setShareExpiry(120);
                                                        }}
                                                        className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-emerald-500 transition-opacity"
                                                        title="Share attendance link"
                                                    >
                                                        <Link2 className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteColumn(col.id)}
                                                        className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-500 transition-opacity"
                                                        title="Delete column"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </th>
                                        ))}
                                        <th className="p-3 border-b border-zinc-200 dark:border-zinc-800 w-full" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {(loading || loadingAttendance) ? (
                                        [...Array(10)].map((_, i) => (
                                            <tr key={i} className={cn("border-b border-zinc-200 dark:border-zinc-800", i % 2 === 0 ? "bg-white dark:bg-zinc-900" : "bg-zinc-50/30 dark:bg-zinc-900/40")}>
                                                <td className="p-3 border-r border-zinc-200 dark:border-zinc-800 sticky left-0 z-10 bg-inherit min-w-[250px]">
                                                    <div className="flex items-center gap-3">
                                                        <Skeleton className="w-7 h-7 rounded-full" />
                                                        <div className="flex flex-col gap-1.5 flex-1">
                                                            <Skeleton className="h-4 w-3/4" />
                                                            <Skeleton className="h-3 w-1/2" />
                                                        </div>
                                                    </div>
                                                </td>
                                                {filteredAttendanceCols.length > 0 ? (
                                                    filteredAttendanceCols.map(col => (
                                                        <td key={col.id} className="p-1.5 border-r border-zinc-200 dark:border-zinc-800">
                                                            <Skeleton className="h-8 w-full rounded" />
                                                        </td>
                                                    ))
                                                ) : (
                                                    [...Array(3)].map((_, j) => (
                                                        <td key={j} className="p-1.5 border-r border-zinc-200 dark:border-zinc-800">
                                                            <Skeleton className="h-8 w-full rounded opacity-40" />
                                                        </td>
                                                    ))
                                                )}
                                                <td className="w-full" />
                                            </tr>
                                        ))
                                    ) : attendanceStudents.length === 0 ? (
                                        <tr>
                                            <td colSpan={filteredAttendanceCols.length + 2} className="p-8 text-center text-zinc-500 text-sm">
                                                No students found in this view.
                                            </td>
                                        </tr>
                                    ) : (
                                        attendanceStudents.map((student, i) => (
                                            <tr key={student.id} className={cn("hover:bg-zinc-50/50 dark:hover:bg-zinc-800/30 transition-colors group", i % 2 === 0 ? "bg-white dark:bg-zinc-900" : "bg-zinc-50/30 dark:bg-zinc-900/40")}>
                                                <td className={cn("p-2 px-3 border-b border-r border-zinc-200 dark:border-zinc-800 font-medium text-sm text-zinc-900 dark:text-zinc-100 sticky left-0 z-10 flex items-center gap-3", i % 2 === 0 ? "bg-white dark:bg-zinc-900" : "bg-zinc-50 dark:bg-zinc-900")}>
                                                    <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center font-bold text-xs shrink-0">
                                                        {student.name.charAt(0)}
                                                    </div>
                                                    <div className="flex flex-col min-w-0">
                                                        <span className="truncate leading-tight">{student.name}</span>
                                                        <span className="text-[10px] text-zinc-400 font-mono leading-tight">{student.enrollment_number}</span>
                                                    </div>
                                                </td>
                                                {filteredAttendanceCols.map(col => {
                                                    const record = attendanceRecords.find(r => r.student_id === student.id && r.column_id === col.id);
                                                    const status = record?.status || 'absent';
                                                    
                                                    const isEnrolled = !student.subject_ids || !col.subject_id || student.subject_ids.includes(col.subject_id);

                                                    let badgeClass = "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 border-transparent hover:border-zinc-300 dark:hover:border-zinc-600";
                                                    let displayText = "-";
                                                    let isDisabled = false;
                                                    
                                                    if (!isEnrolled) {
                                                        badgeClass = "bg-zinc-50 text-zinc-400 dark:bg-zinc-900/30 dark:text-zinc-500 border border-zinc-100 dark:border-zinc-800 cursor-not-allowed font-normal";
                                                        displayText = "Not Enrolled";
                                                        isDisabled = true;
                                                    } else if (status === 'present') {
                                                        badgeClass = "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/80 hover:bg-emerald-200 dark:hover:bg-emerald-900/60";
                                                        displayText = "Present";
                                                    } else if (status === 'absent') {
                                                        badgeClass = "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 border border-red-200 dark:border-red-800/80 hover:bg-red-200 dark:hover:bg-red-900/60";
                                                        displayText = "Absent";
                                                    } else if (status === 'late') {
                                                        badgeClass = "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400 border border-orange-200 dark:border-orange-800/80 hover:bg-orange-200 dark:hover:bg-orange-900/60";
                                                        displayText = "Late";
                                                    }
                                                    
                                                    return (
                                                        <td key={col.id} className="p-1.5 border-b border-r border-zinc-200 dark:border-zinc-800 text-center">
                                                            <button 
                                                                disabled={isDisabled}
                                                                onClick={() => handleToggleAttendance(student.id, col.id, status)}
                                                                className={cn(
                                                                    "w-full h-8 rounded border text-xs font-semibold transition-all active:scale-95 flex items-center justify-center",
                                                                    badgeClass
                                                                )}
                                                            >
                                                                {displayText}
                                                            </button>
                                                        </td>
                                                    );
                                                })}
                                                <td className="border-b border-zinc-200 dark:border-zinc-800" />
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'tests' && (
                    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden flex flex-col h-full shadow-sm">
                        <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex justify-between items-center bg-zinc-50/50 dark:bg-zinc-900/50">
                            <h2 className="font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                                <ClipboardList className="w-5 h-5 text-blue-500" />
                                Interactive Test Scorer
                            </h2>
                            <button 
                                onClick={handleAddTestColumn}
                                className="flex items-center gap-1.5 bg-white border border-zinc-200 hover:bg-zinc-50 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm"
                            >
                                <Plus className="w-4 h-4" />
                                Add Test
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto">
                            <table className="w-full text-left border-collapse min-w-max relative">
                                <thead className="bg-zinc-50 dark:bg-zinc-800/50 sticky top-0 z-20 shadow-sm shadow-zinc-200/50 dark:shadow-black/20">
                                    <tr>
                                        <th className="p-3 border-b border-r border-zinc-200 dark:border-zinc-800 font-semibold text-xs tracking-wider text-zinc-500 uppercase sticky left-0 bg-zinc-50 dark:bg-zinc-900/95 backdrop-blur-sm z-30 w-64 min-w-[250px]">Student Name</th>
                                        {filteredTestCols.map(col => (
                                            <th key={col.id} className="p-3 border-b border-r border-zinc-200 dark:border-zinc-800 font-semibold text-xs tracking-wider text-zinc-500 uppercase min-w-[120px] group">
                                                <div className="flex items-center gap-2 w-full">
                                                    <div className="flex flex-col truncate flex-1">
                                                        <span className="text-zinc-700 dark:text-zinc-200">{col.label}</span>
                                                        {(col.batch?.name || col.subject?.name || col.total_marks) && (
                                                            <span className="text-[10px] text-zinc-400 font-normal truncate normal-case tracking-normal">
                                                                {[col.batch?.name, col.subject?.name].filter(Boolean).join(' • ')} {col.total_marks ? `(${col.total_marks}m)` : ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button 
                                                        onClick={() => handleRenameTestColumn(col.id, col.label, col.batch_id, col.subject_id, col.total_marks)}
                                                        className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-blue-500 transition-opacity ml-auto"
                                                        title="Rename test"
                                                    >
                                                        <Edit3 className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setShareTestModal({ colId: col.id, label: col.label });
                                                            setShareTestLink('');
                                                            setShareTestCode('');
                                                            setShareTestExpiry(120);
                                                        }}
                                                        className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-emerald-500 transition-opacity"
                                                        title="Share test scores link"
                                                    >
                                                        <Link2 className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteTestColumn(col.id)}
                                                        className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-500 transition-opacity"
                                                        title="Delete test"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </th>
                                        ))}
                                        <th className="p-3 border-b border-zinc-200 dark:border-zinc-800 w-full" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {(loading || loadingTestsData) ? (
                                        [...Array(10)].map((_, i) => (
                                            <tr key={i} className={cn("border-b border-zinc-200 dark:border-zinc-800", i % 2 === 0 ? "bg-white dark:bg-zinc-900" : "bg-zinc-50/30 dark:bg-zinc-900/40")}>
                                                <td className="p-3 border-r border-zinc-200 dark:border-zinc-800 sticky left-0 z-10 bg-inherit min-w-[250px]">
                                                    <div className="flex items-center gap-3">
                                                        <Skeleton className="w-7 h-7 rounded-full" />
                                                        <div className="flex flex-col gap-1.5 flex-1">
                                                            <Skeleton className="h-4 w-3/4" />
                                                            <Skeleton className="h-3 w-1/2" />
                                                        </div>
                                                    </div>
                                                </td>
                                                {filteredTestCols.length > 0 ? (
                                                    filteredTestCols.map(col => (
                                                        <td key={col.id} className="p-1.5 border-r border-zinc-200 dark:border-zinc-800">
                                                            <Skeleton className="h-8 w-full rounded" />
                                                        </td>
                                                    ))
                                                ) : (
                                                    [...Array(3)].map((_, j) => (
                                                        <td key={j} className="p-1.5 border-r border-zinc-200 dark:border-zinc-800">
                                                            <Skeleton className="h-8 w-full rounded opacity-40" />
                                                        </td>
                                                    ))
                                                )}
                                                <td className="w-full" />
                                            </tr>
                                        ))
                                    ) : testStudents.length === 0 ? (
                                        <tr>
                                            <td colSpan={filteredTestCols.length + 2} className="p-8 text-center text-zinc-500 text-sm">
                                                No students found in this view.
                                            </td>
                                        </tr>
                                    ) : (
                                        testStudents.map((student, i) => (
                                            <tr key={student.id} className={cn("hover:bg-zinc-50/50 dark:hover:bg-zinc-800/30 transition-colors group", i % 2 === 0 ? "bg-white dark:bg-zinc-900" : "bg-zinc-50/30 dark:bg-zinc-900/40")}>
                                                <td className={cn("p-2 px-3 border-b border-r border-zinc-200 dark:border-zinc-800 font-medium text-sm text-zinc-900 dark:text-zinc-100 sticky left-0 z-10 flex items-center gap-3", i % 2 === 0 ? "bg-white dark:bg-zinc-900" : "bg-zinc-50 dark:bg-zinc-900")}>
                                                    <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center font-bold text-xs shrink-0">
                                                        {student.name.charAt(0)}
                                                    </div>
                                                    <div className="flex flex-col min-w-0">
                                                        <span className="truncate leading-tight">{student.name}</span>
                                                        <span className="text-[10px] text-zinc-400 font-mono leading-tight">{student.enrollment_number}</span>
                                                    </div>
                                                </td>
                                                {filteredTestCols.map(col => {
                                                    const record = allScores.find(r => r.student_id === student.id && r.column_id === col.id);
                                                    return (
                                                        <TestScoreCell
                                                            key={`${student.id}-${col.id}`}
                                                            student={student}
                                                            testCol={col}
                                                            record={record}
                                                            onUpdate={handleUpdateMarkInline}
                                                        />
                                                    );
                                                })}
                                                <td className="border-b border-zinc-200 dark:border-zinc-800" />
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}




            </div>

            {/* Modals - Simplified for the main app theme */}
            {/* Add Student Modal */}
            {showAddModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-zinc-900 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
                            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">Add New Student</h2>
                            <button onClick={() => setShowAddModal(false)} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleAddStudent} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Full Name</label>
                                <input 
                                    required
                                    type="text" 
                                    className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={newStudent.name}
                                    onChange={(e) => setNewStudent({...newStudent, name: e.target.value})}
                                    placeholder="Enter full name"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Email</label>
                                    <input 
                                        type="email" 
                                        className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={newStudent.email}
                                        onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                                        placeholder="Optional"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Enrollment #</label>
                                    <input 
                                        type="text" 
                                        className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={newStudent.enrollment_number}
                                        onChange={(e) => setNewStudent({...newStudent, enrollment_number: e.target.value})}
                                        placeholder="ST-001"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Branch</label>
                                    <select 
                                        required
                                        className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                        value={newStudent.branch_id || ''}
                                        onChange={(e) => setNewStudent({...newStudent, branch_id: e.target.value})}
                                    >
                                        <option value="">Select Branch</option>
                                        {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Course</label>
                                    <select 
                                        required
                                        className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                        value={newStudent.course_id || ''}
                                        onChange={(e) => {
                                            const selectedCourseId = e.target.value;
                                            let nextBatchId = newStudent.batch_id;
                                            if (selectedCourseId) {
                                                const selectedBatch = batches.find(b => b.id === newStudent.batch_id);
                                                if (selectedBatch && selectedBatch.course_id !== selectedCourseId) {
                                                    nextBatchId = '';
                                                }
                                            }
                                            setNewStudent({...newStudent, course_id: selectedCourseId, batch_id: nextBatchId});
                                        }}
                                    >
                                        <option value="">Select Course</option>
                                        {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Batch</label>
                                <select 
                                    required
                                    className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                    value={newStudent.batch_id || ''}
                                    onChange={(e) => {
                                        const selectedBatchId = e.target.value;
                                        const selectedBatch = batches.find(b => b.id === selectedBatchId);
                                        const updatedCourseId = selectedBatch?.course_id || newStudent.course_id;
                                        setNewStudent({...newStudent, batch_id: selectedBatchId, course_id: updatedCourseId});
                                    }}
                                >
                                    <option value="">Select Batch</option>
                                    {(newStudent.course_id ? batches.filter(b => b.course_id === newStudent.course_id) : batches).map(b => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </div>
                            
                            {newStudent.course_id && (
                                <div className="space-y-2">
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider">Subject Enrollment</label>
                                    <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-xl p-3 border border-zinc-100 dark:border-zinc-800 space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                                        <label className="flex items-center gap-2 text-xs font-semibold text-zinc-700 dark:text-zinc-300 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                                                checked={!newStudent.subject_ids}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setNewStudent({ ...newStudent, subject_ids: null });
                                                    } else {
                                                        setNewStudent({ ...newStudent, subject_ids: [] });
                                                    }
                                                }}
                                            />
                                            All Subjects (Default)
                                        </label>
                                        {!newStudent.subject_ids && (
                                            <div className="pl-6 text-[10px] text-zinc-400 italic">
                                                Student will be enrolled in all current and future subjects of this course.
                                            </div>
                                        )}
                                        {newStudent.subject_ids && (
                                            <div className="space-y-1.5 pl-6 border-l border-zinc-200 dark:border-zinc-700">
                                                {subjects.filter(s => s.course_id === newStudent.course_id).map(subject => (
                                                    <label key={subject.id} className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400 cursor-pointer">
                                                        <input 
                                                            type="checkbox"
                                                            className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                                                            checked={newStudent.subject_ids.includes(subject.id)}
                                                            onChange={(e) => {
                                                                const current = newStudent.subject_ids || [];
                                                                const updated = e.target.checked
                                                                    ? [...current, subject.id]
                                                                    : current.filter(id => id !== subject.id);
                                                                setNewStudent({ ...newStudent, subject_ids: updated });
                                                            }}
                                                        />
                                                        {subject.name}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="pt-4">
                                <button 
                                    type="submit"
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-blue-500/20 active:scale-[0.98] transition-all"
                                >
                                    Confirm Enrollment
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Student Modal */}
            {showEditModal && editStudent && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-zinc-900 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
                            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">Edit Student Details</h2>
                            <button onClick={() => setShowEditModal(false)} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleEditStudent} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Full Name</label>
                                <input 
                                    required
                                    type="text" 
                                    className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={editStudent.name}
                                    onChange={(e) => setEditStudent({...editStudent, name: e.target.value})}
                                    placeholder="Enter full name"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Email</label>
                                    <input 
                                        type="email" 
                                        className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={editStudent.email}
                                        onChange={(e) => setEditStudent({...editStudent, email: e.target.value})}
                                        placeholder="Optional"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Enrollment #</label>
                                    <input 
                                        type="text" 
                                        className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={editStudent.enrollment_number}
                                        onChange={(e) => setEditStudent({...editStudent, enrollment_number: e.target.value})}
                                        placeholder="ST-001"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Branch</label>
                                    <select 
                                        required
                                        className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                        value={editStudent.branch_id || ''}
                                        onChange={(e) => setEditStudent({...editStudent, branch_id: e.target.value})}
                                    >
                                        <option value="">Select Branch</option>
                                        {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Course</label>
                                    <select 
                                        required
                                        className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                        value={editStudent.course_id || ''}
                                        onChange={(e) => {
                                            const selectedCourseId = e.target.value;
                                            let nextBatchId = editStudent.batch_id;
                                            if (selectedCourseId) {
                                                const selectedBatch = batches.find(b => b.id === editStudent.batch_id);
                                                if (selectedBatch && selectedBatch.course_id !== selectedCourseId) {
                                                    nextBatchId = '';
                                                }
                                            }
                                            setEditStudent({...editStudent, course_id: selectedCourseId, batch_id: nextBatchId, subject_ids: null});
                                        }}
                                    >
                                        <option value="">Select Course</option>
                                        {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Batch</label>
                                <select 
                                    required
                                    className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                    value={editStudent.batch_id || ''}
                                    onChange={(e) => {
                                        const selectedBatchId = e.target.value;
                                        const selectedBatch = batches.find(b => b.id === selectedBatchId);
                                        const updatedCourseId = selectedBatch?.course_id || editStudent.course_id;
                                        setEditStudent({...editStudent, batch_id: selectedBatchId, course_id: updatedCourseId, subject_ids: null});
                                    }}
                                >
                                    <option value="">Select Batch</option>
                                    {(editStudent.course_id ? batches.filter(b => b.course_id === editStudent.course_id) : batches).map(b => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </div>
                            
                            {editStudent.course_id && (
                                <div className="space-y-2">
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider">Subject Enrollment</label>
                                    <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-xl p-3 border border-zinc-100 dark:border-zinc-800 space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                                        <label className="flex items-center gap-2 text-xs font-semibold text-zinc-700 dark:text-zinc-300 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                                                checked={!editStudent.subject_ids}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setEditStudent({ ...editStudent, subject_ids: null });
                                                    } else {
                                                        setEditStudent({ ...editStudent, subject_ids: [] });
                                                    }
                                                }}
                                            />
                                            All Subjects (Default)
                                        </label>
                                        {!editStudent.subject_ids && (
                                            <div className="pl-6 text-[10px] text-zinc-400 italic">
                                                Student will be enrolled in all current and future subjects of this course.
                                            </div>
                                        )}
                                        {editStudent.subject_ids && (
                                            <div className="space-y-1.5 pl-6 border-l border-zinc-200 dark:border-zinc-700">
                                                {subjects.filter(s => s.course_id === editStudent.course_id).map(subject => (
                                                    <label key={subject.id} className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400 cursor-pointer">
                                                        <input 
                                                            type="checkbox"
                                                            className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                                                            checked={editStudent.subject_ids.includes(subject.id)}
                                                            onChange={(e) => {
                                                                const current = editStudent.subject_ids || [];
                                                                const updated = e.target.checked
                                                                    ? [...current, subject.id]
                                                                    : current.filter(id => id !== subject.id);
                                                                setEditStudent({ ...editStudent, subject_ids: updated });
                                                            }}
                                                        />
                                                        {subject.name}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="pt-4">
                                <button 
                                    type="submit"
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-blue-500/20 active:scale-[0.98] transition-all"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* Marks/Performance Modal was removed as the performance panel is now rendered inline in the Overview tab */}

            {/* ── Share Link Modal ───────────────────────────────────── */}
            {/* ── Share Link Modal ───────────────────────────────────── */}
            {shareModal && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-zinc-900 w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                                    <Link2 className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wider">Share Attendance</p>
                                    <p className="text-sm font-bold text-zinc-900 dark:text-zinc-50 leading-tight">{shareModal.label}</p>
                                </div>
                            </div>
                            <button onClick={() => { setShareModal(null); setShareLink(''); }} className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-5 space-y-4">
                            {/* Expiry selector */}
                            <div>
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                    <Clock className="w-3.5 h-3.5" /> Link Expiry
                                </label>
                                <div className="grid grid-cols-4 gap-2">
                                    {[30, 60, 120, 240].map(mins => (
                                        <button
                                            key={mins}
                                            onClick={() => { setShareExpiry(mins); setShareLink(''); }}
                                            className={cn(
                                                "py-2 rounded-lg text-xs font-semibold border transition-all",
                                                shareExpiry === mins
                                                    ? "bg-emerald-500 text-white border-emerald-500 shadow-sm"
                                                    : "bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-zinc-700 hover:border-emerald-400"
                                            )}
                                        >
                                            {mins < 60 ? `${mins}m` : `${mins / 60}h`}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Generate button */}
                            <button
                                onClick={() => handleShareLink(shareModal.colId, shareExpiry)}
                                disabled={loadingShare}
                                className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold py-2.5 rounded-xl transition-all shadow-md shadow-emerald-200 active:scale-[0.98] flex items-center justify-center gap-2"
                            >
                                {loadingShare ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                                {loadingShare ? 'Generating…' : 'Generate Link'}
                            </button>

                            {/* Result */}
                            {shareLink && (
                                <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Shareable URL</label>
                                        <div className="flex items-center gap-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-2.5">
                                            <p className="flex-1 text-xs font-mono text-zinc-600 dark:text-zinc-400 truncate">{shareLink}</p>
                                            <button
                                                onClick={handleCopyLink}
                                                className="shrink-0 p-1.5 text-zinc-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Copy link"
                                            >
                                                <Copy className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="flex flex-col items-center justify-center p-4 bg-zinc-50 dark:bg-zinc-800/40 rounded-2xl border border-zinc-100 dark:border-zinc-800/50">
                                        <div className="bg-white p-2.5 rounded-xl shadow-sm border border-zinc-200">
                                            <img 
                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(shareLink)}`}
                                                alt="Attendance QR Code"
                                                className="w-[180px] h-[180px] block"
                                            />
                                        </div>
                                        <p className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400 mt-2.5 uppercase tracking-wider">
                                            Scan QR Code to Register
                                        </p>
                                    </div>
                                    
                                    <p className="text-[10px] text-zinc-400 text-center">
                                        Expires in {shareExpiry < 60 ? `${shareExpiry} min` : `${shareExpiry / 60} hr`}. Students do not need to log in.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Share Test Scores Link Modal ─────────────────────────── */}
            {shareTestModal && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-zinc-900 w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                                    <Link2 className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wider">Share Test Scores</p>
                                    <p className="text-sm font-bold text-zinc-900 dark:text-zinc-50 leading-tight">{shareTestModal.label}</p>
                                </div>
                            </div>
                            <button onClick={() => { setShareTestModal(null); setShareTestLink(''); setShareTestCode(''); }} className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-5 space-y-4">
                            {/* Expiry selector */}
                            <div>
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                    <Clock className="w-3.5 h-3.5" /> Link Expiry
                                </label>
                                <div className="grid grid-cols-4 gap-2">
                                    {[30, 60, 120, 240].map(mins => (
                                        <button
                                            key={mins}
                                            onClick={() => { setShareTestExpiry(mins); setShareTestLink(''); setShareTestCode(''); }}
                                            className={cn(
                                                "py-2 rounded-lg text-xs font-semibold border transition-all",
                                                shareTestExpiry === mins
                                                    ? "bg-emerald-500 text-white border-emerald-500 shadow-sm"
                                                    : "bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-zinc-700 hover:border-emerald-400"
                                            )}
                                        >
                                            {mins < 60 ? `${mins}m` : `${mins / 60}h`}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Generate button */}
                            <button
                                onClick={() => handleShareTestLink(shareTestModal.colId, shareTestExpiry)}
                                disabled={loadingShareTest}
                                className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold py-2.5 rounded-xl transition-all shadow-md shadow-emerald-200 active:scale-[0.98] flex items-center justify-center gap-2"
                            >
                                {loadingShareTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                                {loadingShareTest ? 'Generating…' : 'Generate Link'}
                            </button>

                            {/* Result */}
                            {shareTestLink && (
                                <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Access Code</label>
                                        <div className="flex items-center justify-between bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-3">
                                            <p className="text-lg font-mono font-bold tracking-widest text-zinc-800 dark:text-zinc-100 flex-1 text-center">{shareTestCode}</p>
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(shareTestCode);
                                                    toast.success('Access code copied!');
                                                }}
                                                className="shrink-0 p-1.5 text-zinc-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Copy code"
                                            >
                                                <Copy className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <p className="text-[10px] text-zinc-400 mt-1.5 text-center">
                                            Users must enter this code to view or edit the test scores.
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Shareable URL</label>
                                        <div className="flex items-center gap-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-2.5">
                                            <p className="flex-1 text-xs font-mono text-zinc-600 dark:text-zinc-400 truncate">{shareTestLink}</p>
                                            <button
                                                onClick={handleCopyTestLink}
                                                className="shrink-0 p-1.5 text-zinc-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Copy link"
                                            >
                                                <Copy className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="flex flex-col items-center justify-center p-4 bg-zinc-50 dark:bg-zinc-800/40 rounded-2xl border border-zinc-100 dark:border-zinc-800/50">
                                        <div className="bg-white p-2.5 rounded-xl shadow-sm border border-zinc-200">
                                            <img 
                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(shareTestLink)}`}
                                                alt="Test Scores QR Code"
                                                className="w-[180px] h-[180px] block"
                                            />
                                        </div>
                                        <p className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400 mt-2.5 uppercase tracking-wider">
                                            Scan QR Code to View Scores
                                        </p>
                                    </div>
                                    
                                    <p className="text-[10px] text-zinc-400 text-center">
                                        Expires in {shareTestExpiry < 60 ? `${shareTestExpiry} min` : `${shareTestExpiry / 60} hr`}.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Test Column Modal ───────────────────────────────────── */}
            {showTestModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-zinc-900 w-full max-w-md rounded-2xl shadow-xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
                            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">{testForm.id ? "Edit Test" : "New Test"}</h2>
                            <button onClick={() => setShowTestModal(false)} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors bg-zinc-100 dark:bg-zinc-800 p-2 rounded-full">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={submitTestForm} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Test Name</label>
                                <input 
                                    required
                                    type="text" 
                                    className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={testForm.label}
                                    onChange={(e) => setTestForm({...testForm, label: e.target.value})}
                                    placeholder="e.g. Midterm 1"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Subject</label>
                                    <select 
                                        className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                        value={testForm.subject_id || ''}
                                        onChange={(e) => setTestForm({...testForm, subject_id: e.target.value})}
                                    >
                                        <option value="">Any Subject</option>
                                        {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Batch</label>
                                    <select 
                                        className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                        value={testForm.batch_id || ''}
                                        onChange={(e) => setTestForm({...testForm, batch_id: e.target.value})}
                                    >
                                        <option value="">Any Batch</option>
                                        {batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Total Marks</label>
                                <input 
                                    required
                                    type="text"
                                    inputMode="decimal"
                                    pattern="[0-9]*[.,]?[0-9]*"
                                    className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={testForm.total_marks || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                            setTestForm({...testForm, total_marks: value});
                                        }
                                    }}
                                />
                            </div>
                            <div className="pt-4">
                                <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-blue-500/20 active:scale-[0.98] transition-all">
                                    {testForm.id ? "Save Changes" : "Create Test"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Attendance Column Modal ───────────────────────────────────── */}
            {showAttModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-zinc-900 w-full max-w-md rounded-2xl shadow-xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
                            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">{attForm.id ? "Edit Column" : "New Column"}</h2>
                            <button onClick={() => setShowAttModal(false)} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors bg-zinc-100 dark:bg-zinc-800 p-2 rounded-full">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={submitAttForm} className="p-6 space-y-4">
                            {attForm.id ? (
                                <>
                                    <div>
                                        <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Column Label</label>
                                        <input 
                                            required
                                            type="text" 
                                            className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                            value={attForm.label}
                                            onChange={(e) => setAttForm({...attForm, label: e.target.value})}
                                            placeholder="e.g. Apr 15"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Subject</label>
                                            <select 
                                                className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                                value={attForm.subject_id || ''}
                                                onChange={(e) => setAttForm({...attForm, subject_id: e.target.value})}
                                            >
                                                <option value="">Any Subject</option>
                                                {filteredSubjectsForAtt.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Batch</label>
                                            <select 
                                                className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                                value={attForm.batch_id || ''}
                                                onChange={(e) => setAttForm({...attForm, batch_id: e.target.value})}
                                            >
                                                <option value="">Any Batch</option>
                                                {batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Date</label>
                                        <input 
                                            required
                                            type="date" 
                                            className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
                                            value={attForm.date || ''}
                                            onChange={(e) => {
                                                const newDate = e.target.value;
                                                const oldDateLabel = formatDateToLabel(attForm.date);
                                                const newDateLabel = formatDateToLabel(newDate);
                                                
                                                setAttForm(prev => ({
                                                    ...prev,
                                                    date: newDate,
                                                    label: prev.label === oldDateLabel ? newDateLabel : prev.label
                                                }));
                                            }}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Subject</label>
                                        <select 
                                            required
                                            className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                            value={attForm.subject_id || ''}
                                            onChange={(e) => setAttForm({...attForm, subject_id: e.target.value})}
                                        >
                                            <option value="">Select Subject</option>
                                            {filteredSubjectsForAtt.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Date</label>
                                            <input 
                                                required
                                                type="date" 
                                                className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
                                                value={attForm.date || ''}
                                                onChange={(e) => {
                                                    const newDate = e.target.value;
                                                    const oldDateLabel = formatDateToLabel(attForm.date);
                                                    const newDateLabel = formatDateToLabel(newDate);
                                                    
                                                    setAttForm(prev => ({
                                                        ...prev,
                                                        date: newDate,
                                                        label: prev.label === oldDateLabel ? newDateLabel : prev.label
                                                    }));
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">Column Label</label>
                                            <input 
                                                required
                                                type="text" 
                                                className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                value={attForm.label}
                                                onChange={(e) => setAttForm({...attForm, label: e.target.value})}
                                                placeholder="e.g. Apr 15"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className="pt-4">
                                <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-blue-500/20 active:scale-[0.98] transition-all">
                                    {attForm.id ? "Save Changes" : "Create Column"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
        </>
    );
}
